import React from 'react';
import {useLocation} from "react-router-dom";
import Linkify from 'react-linkify';
import { useAsync } from 'react-async-hook';
import { taskcommentApi } from '../../../apis/taskcomment/taskcommentApi';
import { UpdateTaskCommentModal } from './UpdateTaskCommentModal';
import { DeleteTaskCommentModal } from './DeleteTaskCommentModal';
import icon312 from '../../../assets/images/5_ROOM_TaskBoard_Re3_12.png';
import icon314 from '../../../assets/images/5_ROOM_TaskBoard_Re3_14.png';
import icon419 from '../../../assets/images/5_ROOM_TaskBoard_Re4_19.png';
import { CreateTaskCommentModal } from './CreateTaskCommentModal';
import icon323 from '../../../assets/images/5_ROOM_TaskBoard_Re3_23.png';
import { TaskComment } from '../../../types/types';
import useLoading from '../../../hooks/useLoading';
import { formatOmittedDateTime } from '../../../libs/utils/formatDate';
import icon442 from '../../../assets/images/ROOM_TaskBoard_Re4_42.png';
import {logError} from "../../../libs/utils/logError";
import useAppRouter from "../../../hooks/useAppRouter";

interface TaskCommentIndexProps {
  taskId: number;
}

export const TaskCommentIndex = (props: TaskCommentIndexProps) => {
  const { taskId } = props;
  const withLoading = useLoading();
  const location = useLocation();
  const { goto } = useAppRouter();
  const [taskComments, setTaskComments] = React.useState<TaskComment[]>([]);
  const [taskComment, setTaskComment] = React.useState<
    TaskComment | undefined
  >();
  const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = React.useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);

  const { execute: reloadComment } = useAsync(async () => {
    const fetch = async () => {
      withLoading(async () => {
        try {
          const response = await taskcommentApi.index(taskId, {});
          setTaskComments(response.taskCommentList);
        } catch(e) {
          await logError(
            location.pathname,
            'コメントの取得に失敗しました。',
            e
          );

          goto('internalServerError');
        }
      });
    };
    fetch();
  }, [taskId]);

  const handleCreateClick = async () => {
    await reloadComment();
    setShowCreateModal(true);
  };

  const handleUpdateClick = (comment: TaskComment) => {
    setTaskComment(comment);
    setShowUpdateModal(true);
  };

  const handleDeleteClick = (comment: TaskComment) => {
    setTaskComment(comment);
    setShowDeleteModal(true);
  };

  const handleCreateSubmit = async () => {
    setShowCreateModal(false);
    await reloadComment();
  };

  const handleUpdateSubmit = async () => {
    setShowUpdateModal(false);
    await reloadComment();
  };

  const handleDeleteSubmit = async () => {
    setShowDeleteModal(false);
    await reloadComment();
  };

  return (
    <>
      {showCreateModal && (
        <CreateTaskCommentModal
          {...{
            taskId,
            onSubmitted: handleCreateSubmit,
          }}
        />
      )}
      {showUpdateModal && taskComment && (
        <UpdateTaskCommentModal
          {...{
            taskComment,
            onSubmitted: handleUpdateSubmit,
          }}
        />
      )}
      {showDeleteModal && taskComment && (
        <DeleteTaskCommentModal
          {...{
            taskComment,
            onSubmitted: handleDeleteSubmit,
          }}
        />
      )}
      {taskComments.length > 0 ? (
        <>
          <div className="px-4 text-xs font-semibold text-primary flex items-center">
            <img src={icon419} alt="icon" />
            <div className="ml-2">{`コメント (${
              taskComments && taskComments.length
            })`}</div>
          </div>
          <div className="px-4 divide-y divide-opacity-60">
            {taskComments.length > 0 ? (
              taskComments.map((c) => (
                <div key={c.id} className="py-4">
                  <div className="flex items-start mb-4">
                    <div className="w-12 flex-none">
                      <img src={icon442} alt="icon" />
                    </div>
                    <div className="flex-grow">
                      <p className="comment-content">
                        <Linkify
                          componentDecorator={(
                            decoratedHref,
                            decoratedText,
                            key
                          ) => (
                            <a
                              target="_blank"
                              href={decoratedHref}
                              key={key}
                              rel="noreferrer"
                            >
                              {decoratedText}
                            </a>
                          )}
                        >
                          {c.content}
                        </Linkify>
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end items-center space-x-2">
                    <div className="text-xs">
                      <p>{`${c.createUser} ｜ ${formatOmittedDateTime(
                        c.createDateTime
                      )}`}</p>
                    </div>
                    <button
                      type="button"
                      className="outline-none focus:outline-none"
                      onClick={() => handleUpdateClick(c)}
                    >
                      <img src={icon312} alt="icon" />
                    </button>
                    <button
                      type="button"
                      className="outline-none focus:outline-none"
                      onClick={() => handleDeleteClick(c)}
                    >
                      <img src={icon314} alt="icon" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <></>
            )}
          </div>
          <div className="pt-4 border-t border-primary">
            <div className="px-4 text-sm font-semibold text-primary mr-4">
              <button
                type="button"
                onClick={handleCreateClick}
                className="text-xs text-primary font-semibold outline-none focus:outline-none flex items-center"
              >
                <img src={icon323} alt="icon" />
                <div className="ml-4">コメントを書く</div>
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="px-4 flex items-center">
            <div className="text-xs font-semibold text-primary flex-none w-24 flex items-center">
              <img src={icon419} alt="icon" />
              <div className="ml-2">コメント</div>
            </div>
            <div className="flex-grow flex">
              <button
                type="button"
                className="btn-primary rounded-md text-sm"
                onClick={handleCreateClick}
              >
                コメントを追加
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
